import type { tracking } from '@fireworkhq/proto-contract'

import merge from '@core/lodash/merge'
import type { Consent } from '@core/tracking'
import {
  getGuestId,
  getSession,
  sendPixelTrackingWithSession,
} from '@core/tracking'
import { setConsent } from '@core/tracking/consent'
import {
  getLastAssistanceIdWithAnswer,
  getLastAvaEngagedWithAnswerTimestamp,
  getLastBusinessMembershipIdCookie,
  getLastChannelId,
  getLastChatChannelId,
  getLastEngagedActiveLivestreamId,
  getLastEngagedLivestreamId,
  getLastEngagedTimestamp,
  getLastEngagedVideoId,
  getLastShowroomIdCookie,
} from '@core/tracking/customTracking'

/**
 *
 */
export class Analytics {
  /**
   *
   */
  get guest_id() {
    return getGuestId()
  }

  /**
   *
   */
  get session_id() {
    return getSession().id
  }

  /**
   * @returns An empty array. This method is set to have API parity with the
   *     fallback analytics module. The fallback analytics module has a queue
   *     to keep the events in memory until the analytics module is loaded.
   */
  get eventQueue(): Array<{
    url: string
    data: Record<string, unknown>
    isUserEngagement?: boolean
  }> {
    return []
  }

  /** @returns False. This is not the fallback analytics module. */
  isFallback() {
    return false
  }

  /**
   *
   */
  #trackUserEvent(event: string, data: any) {
    const businessMembershipId = getLastBusinessMembershipIdCookie()
    const showroomId = getLastShowroomIdCookie()
    const mergedData: tracking.shared.Event = merge(
      {},
      {
        event_properties: {
          _last_assistance_id: getLastAssistanceIdWithAnswer(),
          _last_chat_channel_id: getLastChatChannelId(),
          _last_channel_id: getLastChannelId(),
          _last_video_id: getLastEngagedVideoId(),
          _last_live_stream_id: getLastEngagedLivestreamId(),
          _last_active_live_stream_id: getLastEngagedActiveLivestreamId(),
          last_ava_engaged_timestamp: getLastAvaEngagedWithAnswerTimestamp(),
          last_engaged_timestamp: getLastEngagedTimestamp(),
          _business_membership_id: businessMembershipId?.value,
          last_business_membership_id_timestamp:
            businessMembershipId?.createTime,
          _last_showroom_id: showroomId?.value,
          last_showroom_id_timestamp: showroomId?.createTime,
        },
      },
      data,
    )

    sendPixelTrackingWithSession({
      endpoint: {
        url: `/trk/user:${event}`,
        // We don't consider user events as activities.
        isUserEngagement: false,
        consent: 'essential',
      },
      data: mergedData,
      host: undefined,
    })
  }

  /**
   * Sends pixel tracking events. A wrapper of sendPixelTrackingWithSession.
   */
  sendEvent(
    url: string,
    data: Record<string, unknown>,
    isUserEngagement = false,
    consent: Consent = 'essential',
  ): void {
    sendPixelTrackingWithSession({
      endpoint: {
        url,
        isUserEngagement,
        consent,
      },
      data,
      host: undefined,
    })
  }

  /**
   * Publicly exposed method to send user tracking events
   */
  push(data: any): void {
    const event = data?.event

    this.#trackUserEvent(event, {
      event_properties: { user_data: data },
    })
  }

  /**
   * A page viewed event for 3rd parties to help with understanding the purchase journey.
   */
  pageViewed(data: tracking.user.UserData): void {
    this.#trackUserEvent('page_viewed', {
      event_properties: { user_data: data },
    })
  }

  /**
   * An add to cart event for 3rd parties to help with abandoned cart tracking.
   */
  addToCart(data: tracking.user.UserData): void {
    this.#trackUserEvent('add_to_cart', {
      event_properties: { user_data: data },
    })
  }

  /**
   * A remove from cart event for 3rd parties to help with abandoned cart tracking.
   */
  removeFromCart(data: tracking.user.UserData): void {
    this.#trackUserEvent('remove_from_cart', {
      event_properties: { user_data: data },
    })
  }

  /**
   * A purchase event for 3rd parties to help with GMV attribution.
   * This is preferred for custom push events because we can dictate
   * the data shape to help the data team with consistent analytics.
   */
  purchase(data: tracking.user.UserData): void {
    this.#trackUserEvent('purchase', {
      event_properties: { user_data: data },
    })
  }

  /**
   * Allows storing of user's selected consent. Consent is later used to
   * restrict tracking of user.
   */
  setConsent(consent?: unknown): void {
    setConsent(consent)
  }
}
